import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  CalendarClock,
  Video,
  MessageSquare,
  ShieldCheck,
  Clock,
  ArrowRight,
} from "lucide-react";
import { CAL_COM_LINK, CAL_NAMESPACE, loadCalCom } from "@/lib/scheduling";
import BookCallButton from "@/components/site/BookCallButton";

/**
 * Inline Cal.com scheduling embed.
 * The Cal namespace API is fully idempotent — safe to re-run on re-mount.
 */
export default function CalendlySection() {
  const widgetRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    (async () => {
      await loadCalCom();
      if (cancelled || !widgetRef.current || !window.Cal?.ns?.[CAL_NAMESPACE]) return;
      try {
        window.Cal.ns[CAL_NAMESPACE]("inline", {
          elementOrSelector: widgetRef.current,
          config: { layout: "month_view", theme: "dark" },
          calLink: CAL_COM_LINK,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Cal.com inline init failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const perks = [
    { icon: Video, text: "15-min video call · no obligation" },
    { icon: MessageSquare, text: "Review your specific problem reviews" },
    { icon: ShieldCheck, text: "Confirm which reviews qualify for removal" },
    { icon: Clock, text: "Get a realistic timeline & cost estimate" },
  ];

  return (
    <section id="book-call" className="relative py-24 lg:py-32 border-t border-white/5">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(700px 400px at 15% 20%, rgba(16,185,129,0.06), transparent 60%), radial-gradient(500px 300px at 85% 80%, rgba(212,175,55,0.05), transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur px-3 py-1.5 text-[11px] text-white/70"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#D4AF37]" />
              </span>
              <span className="uppercase tracking-[0.2em] font-semibold">
                Talk to a specialist
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-[1.05]"
            >
              Book a <span className="text-[#10B981]">free 15-minute</span> consultation.
            </motion.h2>

            <p className="mt-5 text-white/60 text-base leading-relaxed max-w-md">
              Prefer a live conversation before the audit? Pick a time that works for you.
              A reputation specialist will walk through your Google Business Profile with you
              and confirm exactly which reviews qualify for removal.
            </p>

            <ul className="mt-8 space-y-3">
              {perks.map((p, i) => (
                <motion.li
                  key={p.text}
                  initial={{ opacity: 0, x: -6 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                  className="flex items-center gap-3 text-sm text-white/75"
                >
                  <span className="w-8 h-8 shrink-0 grid place-items-center rounded-lg border border-[#10B981]/30 bg-[#10B981]/[0.08] text-[#10B981]">
                    <p.icon className="w-4 h-4" />
                  </span>
                  {p.text}
                </motion.li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <BookCallButton
                variant="primary"
                label="Open popup scheduler"
                testId="book-call-popup"
              />
              <a
                href="#audit"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-white/15 hover:border-white/30 bg-white/[0.02] hover:bg-white/[0.05] text-white text-sm font-semibold transition-colors"
              >
                Prefer email? Submit the audit form
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-6 inline-flex items-center gap-2 text-[11px] text-white/45">
              <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
              No sales pitch. No credit card. No pressure.
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] overflow-hidden">
              <div className="flex items-center justify-between border-b border-white/5 px-5 py-3 bg-white/[0.02]">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarClock className="w-4 h-4 text-[#10B981]" />
                  <span className="font-display font-semibold text-white">
                    Pick a time
                  </span>
                  <span className="text-white/40">·</span>
                  <span className="text-white/50 text-xs">Powered by Cal.com</span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-white/45">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                  Live availability
                </div>
              </div>

              <div
                ref={widgetRef}
                id="gh-cal-inline"
                style={{ minWidth: "320px", width: "100%", height: "660px", overflow: "auto" }}
                data-testid="cal-inline-widget"
              />
            </div>

            <p className="mt-4 text-center text-[11px] text-white/40">
              Can't find a time that works?{" "}
              <a href="mailto:info@igohigher.com" className="text-white/70 underline decoration-white/20 hover:text-[#10B981]">
                Email us
              </a>{" "}
              — we'll find one that fits.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
